/* Empowerment League — stars, badges, personal rank, site-vs-site standings.
 * ZERO rupee values anywhere. Zero. Trilingual. All internal roles.
 */
import { useEffect, useState } from "react";
import { Trophy, Star, Sparkles, Award } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const T = {
    title:        { en: "Empowerment League",         hi: "एम्पावरमेंट लीग",         bn: "এম্পাওয়ারমেন্ট লিগ" },
    subtitle:     { en: "Judgement earns stars — not spend.", hi: "समझदारी से सितारे मिलते हैं, खर्च से नहीं।", bn: "বুদ্ধিমত্তায় তারা মেলে, খরচে নয়।" },
    my_stars:     { en: "My stars this month",         hi: "इस माह मेरे सितारे",     bn: "এই মাসে আমার তারা" },
    my_rank:      { en: "My rank",                     hi: "मेरी रैंक",              bn: "আমার র‍্যাঙ্ক" },
    next_badge:   { en: "Next badge",                  hi: "अगला बैज",              bn: "পরবর্তী ব্যাজ" },
    leaderboard:  { en: "Leaderboard",                 hi: "लीडरबोर्ड",              bn: "লিডারবোর্ড" },
    sites:        { en: "Site vs. site",               hi: "साइट बनाम साइट",         bn: "সাইট বনাম সাইট" },
    avg_stars:    { en: "Avg stars / person",          hi: "औसत सितारे / व्यक्ति",   bn: "গড় তারা / জন" },
    no_solicit:   { en: "Never ask, nudge, or survey a client for feedback or reviews. Praise only counts when it walks in by itself.",
                    hi: "क्लाइंट से समीक्षा या फ़ीडबैक कभी न मांगें, न इशारा करें, न सर्वे कराएँ। सराहना तभी गिनी जाती है जब वह अपने आप आती है।",
                    bn: "গ্রাহকের কাছ থেকে ফিডব্যাক বা রিভিউ কখনও চাইবেন না, ইঙ্গিতও দেবেন না, সার্ভেও নয়। প্রশংসা তখনই গণ্য যখন সেটা নিজে থেকে আসে।" },
    recent:       { en: "Recent stars",                hi: "हाल के सितारे",          bn: "সাম্প্রতিক তারা" },
    empty:        { en: "No stars yet this month — earn some with a great save!", hi: "इस माह अभी सितारे नहीं — एक बढ़िया रेस्क्यू से पाएँ!", bn: "এই মাসে এখনো তারা নেই — চমৎকার একটি Save দিয়ে পান!" },
};

const BADGE_ORDER = [
    { key: "bronze_client_hero", threshold: 10, label: "Bronze",  colour: "bg-orange-100 text-orange-800",  ring: "ring-orange-400"  },
    { key: "silver_client_hero", threshold: 25, label: "Silver",  colour: "bg-gray-200 text-gray-800",       ring: "ring-gray-400"    },
    { key: "gold_client_hero",   threshold: 50, label: "Gold",    colour: "bg-amber-100 text-amber-800",     ring: "ring-amber-400"   },
];

function langOf(u) { return u?.language_preference || u?.language || "en"; }
function tr(u, k) { return T[k]?.[langOf(u)] || T[k]?.en || k; }

export default function FundLeaguePage() {
    const { user } = useAuth();
    const [lb, setLb]         = useState(null);
    const [mine, setMine]     = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const [lbRes, myRes] = await Promise.all([
                    api.get("/fund/leaderboard"),
                    api.get("/fund/my-stars"),
                ]);
                setLb(lbRes.data);
                setMine(myRes.data);
            } catch (e) { toast.error(apiError(e)); }
            finally { setLoading(false); }
        }
        load();
    }, []);

    if (loading || !lb || !mine) {
        return <div className="mx-auto max-w-4xl p-6"><div className="h-72 rounded-2xl bg-white animate-pulse" /></div>;
    }

    const myRank = (lb.people || []).findIndex((p) => p.user_id === user.id) + 1 || null;
    const nextBadge = BADGE_ORDER.find((b) => b.threshold > mine.points) || null;
    const currentBadge = [...BADGE_ORDER].reverse().find((b) => b.threshold <= mine.points) || null;

    return (
        <div className="mx-auto max-w-4xl px-4 py-6" data-testid="fund-league-page">
            <div className="flex items-baseline justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-[#1E3355] inline-flex items-center gap-2"><Trophy className="w-7 h-7 text-amber-500" /> {tr(user, "title")}</h1>
                    <div className="text-sm text-gray-500">{tr(user, "subtitle")}</div>
                </div>
            </div>

            {/* My stars card */}
            <div className="mt-4 rounded-3xl bg-gradient-to-br from-amber-100 via-white to-emerald-50 border border-amber-200 shadow-sm p-5" data-testid="fund-league-mine">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className={`relative rounded-full h-20 w-20 flex items-center justify-center bg-white ring-4 ${currentBadge?.ring || "ring-gray-200"} shadow-inner`}>
                            <Star className="w-9 h-9 text-amber-500 fill-amber-400" />
                            <span className="absolute -bottom-1 -right-1 text-xs font-bold rounded-full bg-[#1E3355] text-white px-2 py-0.5">★ {mine.points}</span>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest text-gray-500">{tr(user, "my_stars")}</div>
                            <div className="text-3xl font-bold text-[#1E3355]" data-testid="fund-league-my-points">{mine.points}</div>
                            {currentBadge && <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold mt-1 ${currentBadge.colour}`}><Award className="w-3 h-3" /> {currentBadge.label} Client Hero</div>}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {myRank && (
                            <div className="text-center">
                                <div className="text-xs uppercase tracking-widest text-gray-500">{tr(user, "my_rank")}</div>
                                <div className="text-2xl font-bold text-[#1E3355]" data-testid="fund-league-my-rank">#{myRank}</div>
                            </div>
                        )}
                        {nextBadge && (
                            <div className="text-center">
                                <div className="text-xs uppercase tracking-widest text-gray-500">{tr(user, "next_badge")}</div>
                                <div className="text-sm font-semibold text-[#1E3355]">{nextBadge.label}</div>
                                <div className="mt-1 h-1.5 w-24 rounded-full bg-gray-200 overflow-hidden">
                                    <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, (mine.points / nextBadge.threshold) * 100)}%` }} />
                                </div>
                                <div className="text-[10px] text-gray-500 mt-0.5">{nextBadge.threshold - mine.points} pts to go</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Recent stars strip */}
                <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">{tr(user, "recent")}</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="fund-league-my-recent">
                        {(mine.entries || []).slice(0, 8).map((s) => (
                            <span key={s.id} className="inline-flex items-center gap-1 rounded-full bg-white border border-amber-200 px-2 py-0.5 text-[10px] text-amber-800 shadow-sm">
                                <Sparkles className="w-3 h-3" /> +{s.points} · {s.reason.replace(/_/g, " ")}
                            </span>
                        ))}
                        {(!mine.entries || mine.entries.length === 0) && <span className="text-[11px] italic text-gray-500">{tr(user, "empty")}</span>}
                    </div>
                </div>
            </div>

            {/* No-solicitation rule text */}
            <div className="mt-4 rounded-2xl bg-white border-l-4 border-red-500 p-3 text-xs italic text-gray-700 shadow-sm" data-testid="fund-league-no-solicit">
                {tr(user, "no_solicit")}
            </div>

            {/* Leaderboard */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden" data-testid="fund-league-people">
                    <div className="p-3 border-b border-gray-100 text-sm uppercase tracking-widest text-gray-500 font-bold">{tr(user, "leaderboard")}</div>
                    <ol className="divide-y divide-gray-100">
                        {(lb.people || []).map((p, i) => {
                            const isMe = p.user_id === user.id;
                            return (
                                <li key={p.user_id} className={`flex items-center gap-3 px-3 py-2 ${isMe ? "bg-amber-50" : ""}`} data-testid={`fund-league-row-${i}`}>
                                    <span className="w-7 text-right font-bold text-gray-400">{i + 1}.</span>
                                    <span className={`flex-1 truncate ${isMe ? "font-bold text-[#1E3355]" : "text-[#1E3355]"}`}>{p.user_name}</span>
                                    {p.badge && (
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                            p.badge === "gold_client_hero" ? "bg-amber-100 text-amber-800" :
                                            p.badge === "silver_client_hero" ? "bg-gray-200 text-gray-800" : "bg-orange-100 text-orange-800"
                                        }`}>{p.badge.replace("_client_hero","").toUpperCase()}</span>
                                    )}
                                    <span className="ml-1 text-amber-600 font-bold flex items-center gap-0.5"><Star className="w-3.5 h-3.5 fill-amber-400" /> {p.points}</span>
                                </li>
                            );
                        })}
                        {(!lb.people || lb.people.length === 0) && <li className="p-3 text-center italic text-gray-400">{tr(user, "empty")}</li>}
                    </ol>
                </div>

                <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden" data-testid="fund-league-sites">
                    <div className="p-3 border-b border-gray-100 text-sm uppercase tracking-widest text-gray-500 font-bold">{tr(user, "sites")}</div>
                    <div className="p-3 space-y-3">
                        {(lb.site_standings || []).map((s, i) => (
                            <div key={s.site_id} data-testid={`fund-league-site-${i}`}>
                                <div className="flex items-baseline justify-between text-sm">
                                    <span className="text-[#1E3355] font-medium">{s.site_id?.slice(0, 8)}</span>
                                    <span className="font-bold text-amber-600">★ {s.avg_per_person} <span className="text-[10px] text-gray-400 font-normal">{tr(user, "avg_stars")}</span></span>
                                </div>
                                <div className="mt-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                                    <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, s.avg_per_person * 10)}%` }} />
                                </div>
                            </div>
                        ))}
                        {(!lb.site_standings || lb.site_standings.length === 0) && <div className="italic text-gray-400 text-sm">—</div>}
                    </div>
                </div>
            </div>
        </div>
    );
}
